import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { Helmet } from 'react-helmet';
import Footer from '../../components/layout/Footer';
import { motion } from 'framer-motion';
import Button from '../../components/ui/Button';

// Background image for the page
const backgroundImage = 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80';

const PageContainer = styled(motion.div)`
  background-color: #000;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
`;

const HeroSection = styled(motion.div)`
  background-color: #000;
  height: 20vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  position: relative;
  text-align: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.4);
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  width: 100%;
  padding: 0 2rem;
`;

const BackLink = styled(Link)`
  position: absolute;
  top: 2rem;
  left: 2rem;
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  font-size: 1rem;
  z-index: 10;
  transition: transform 0.3s ease;
  
  &:hover {
    transform: translateX(-5px);
  }
  
  &::before {
    content: '←';
    margin-right: 8px;
    font-size: 1.2rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  font-style: italic;
  text-align: center;
  color: #0d9488;
`;

const Description = styled.p`
  font-size: 1.2rem;
  line-height: 1.7;
  margin-bottom: 2rem;
  text-align: center;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  color: #e0e0e0;
`;

const ContentSection = styled.div`
  flex: 1;
  width: 100%;
  padding: 4rem 0;
  background-image: linear-gradient(rgba(0, 0, 0, 0.8), rgba(0, 0, 0, 0.8)), url(${backgroundImage});
  background-size: cover;
  background-position: center;
  background-attachment: fixed;
  color: #fff;
`;

const ContentContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.2rem;
  margin: 4rem 0 2rem;
  text-align: center;
  color: #0d9488;
  font-style: italic;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin: 4rem 0;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background-color: rgba(13, 148, 136, 0.1);
  border: 1px solid #0d9488;
  border-radius: 8px;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3);
  }
`;

const BenefitIcon = styled.div`
  width: 60px;
  height: 60px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 1.5rem;
  
  svg {
    width: 100%;
    height: 100%;
    color: #0d9488;
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #0d9488;
  font-style: italic;
`;

const BenefitDescription = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const FeaturesContainer = styled.div`
  margin: 4rem 0;
`;

const FeatureItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 2.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const FeatureNumber = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #0d9488;
  color: white;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
  font-size: 1.5rem;
  margin-right: 2rem;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    margin-bottom: 1rem;
  }
`;

const FeatureContent = styled.div`
  flex: 1;
`;

const FeatureTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 0.8rem;
  color: #0d9488;
`;

const FeatureDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #e0e0e0;
`;

const CTASection = styled.div`
  margin-top: 6rem;
  text-align: center;
`;

const SmartWorking: React.FC = () => {
  const { language } = useLanguage();
  
  // Animation variants
  const pageVariants = {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 }
  };

  const heroVariants = {
    initial: { y: -50, opacity: 0 },
    animate: { y: 0, opacity: 1, transition: { duration: 0.6 } }
  };
  
  const benefits = [
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8h1a4 4 0 0 1 0 8h-1"></path>
          <path d="M2 8h16v9a4 4 0 0 1-4 4H6a4 4 0 0 1-4-4V8z"></path>
          <line x1="6" y1="1" x2="6" y2="4"></line>
          <line x1="10" y1="1" x2="10" y2="4"></line>
          <line x1="14" y1="1" x2="14" y2="4"></line>
        </svg>
      ),
      title: language === 'it' ? 'AMBIENTE ISPIRANTE' : 'INSPIRING ENVIRONMENT',
      description: language === 'it' 
        ? 'Un cambio di scenario può stimolare la creatività e la produttività, offrendo un\'alternativa all\'ambiente familiare o all\'ufficio tradizionale.'
        : 'A change of scenery can stimulate creativity and productivity, offering an alternative to the familiar environment or traditional office.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
          <line x1="8" y1="21" x2="16" y2="21"></line>
          <line x1="12" y1="17" x2="12" y2="21"></line>
        </svg>
      ),
      title: language === 'it' ? 'TECNOLOGIA AVANZATA' : 'ADVANCED TECHNOLOGY',
      description: language === 'it' 
        ? 'Gli spazi sono dotati di connessione internet ad alta velocità e infrastrutture tecnologiche necessarie per il lavoro remoto.'
        : 'Spaces are equipped with high-speed internet connection and technological infrastructures necessary for remote work.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
        </svg>
      ),
      title: language === 'it' ? 'PRIVACY E TRANQUILLITÀ' : 'PRIVACY AND TRANQUILITY',
      description: language === 'it' 
        ? 'Un ambiente privato e tranquillo, ideale per riunioni virtuali, chiamate importanti o semplicemente per concentrarsi su progetti complessi.'
        : 'A private and quiet environment, ideal for virtual meetings, important calls or simply focusing on complex projects.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"></polygon>
        </svg>
      ),
      title: language === 'it' ? 'POSIZIONE STRATEGICA' : 'STRATEGIC LOCATION',
      description: language === 'it' 
        ? 'Gli immobili sono spesso situati in posizioni strategiche, riducendo la necessità di lunghi spostamenti e permettendo di esplorare nuove aree durante le pause.'
        : 'Properties are often located in strategic positions, reducing the need for long journeys and allowing you to explore new areas during breaks.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
          <line x1="8" y1="12" x2="16" y2="12"></line>
          <line x1="12" y1="16" x2="12" y2="16"></line>
          <line x1="12" y1="8" x2="12" y2="8"></line>
        </svg>
      ),
      title: language === 'it' ? 'SPAZI ERGONOMICI' : 'ERGONOMIC SPACES',
      description: language === 'it' 
        ? 'Gli spazi sono progettati con attenzione all\'ergonomia, garantendo comfort durante le lunghe ore di lavoro e riducendo lo stress fisico.'
        : 'Spaces are designed with attention to ergonomics, ensuring comfort during long working hours and reducing physical stress.'
    },
    {
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="7"></circle>
          <polyline points="12 9 12 12 13.5 13.5"></polyline>
          <path d="M16.51 17.35l-.35 3.83a2 2 0 0 1-2 1.82H9.83a2 2 0 0 1-2-1.82l-.35-3.83m.01-10.7l.35-3.83A2 2 0 0 1 9.83 1h4.35a2 2 0 0 1 2 1.82l.35 3.83"></path>
        </svg>
      ),
      title: language === 'it' ? 'FLESSIBILITÀ DI PRENOTAZIONE' : 'BOOKING FLEXIBILITY',
      description: language === 'it' 
        ? 'È possibile prenotare gli spazi per periodi che vanno da poche ore a diversi giorni o settimane, a seconda delle esigenze lavorative.'
        : 'Spaces can be booked for periods ranging from a few hours to several days or weeks, depending on work needs.'
    }
  ];
  
  const features = [
    {
      number: 1,
      title: language === 'it' ? 'CONNESSIONE INTERNET AD ALTA VELOCITÀ' : 'HIGH SPEED INTERNET CONNECTION',
      description: language === 'it' 
        ? 'Una connessione internet affidabile e veloce è essenziale per lo smart working, permettendo di partecipare a videoconferenze senza interruzioni e di accedere rapidamente ai dati e alle applicazioni aziendali.'
        : 'A reliable and fast internet connection is essential for smart working, allowing you to participate in video conferences without interruptions and quickly access company data and applications.'
    },
    {
      number: 2,
      title: language === 'it' ? 'SPAZI DI LAVORO DEDICATI' : 'DEDICATED WORK SPACES',
      description: language === 'it' 
        ? 'Postazioni di lavoro ergonomiche con sedie comode, scrivanie spaziose e illuminazione adeguata, progettate per garantire produttività e benessere durante la giornata lavorativa.'
        : 'Ergonomic workstations with comfortable chairs, spacious desks and adequate lighting, designed to ensure productivity and well-being during the working day.'
    },
    {
      number: 3,
      title: language === 'it' ? 'AREE PER RIUNIONI VIRTUALI' : 'AREAS FOR VIRTUAL MEETINGS',
      description: language === 'it' 
        ? 'Spazi tranquilli dedicati alle videochiamate e alle riunioni virtuali, con sfondi professionali e acustica ottimale per garantire comunicazioni chiare e senza disturbi.'
        : 'Quiet spaces dedicated to video calls and virtual meetings, with professional backgrounds and optimal acoustics to ensure clear and undisturbed communications.'
    },
    {
      number: 4,
      title: language === 'it' ? 'SERVIZI AGGIUNTIVI' : 'ADDITIONAL SERVICES',
      description: language === 'it' 
        ? 'Accesso a servizi come stampanti, scanner e altri strumenti d\'ufficio, oltre a supporto tecnico per risolvere eventuali problemi tecnologici.'
        : 'Access to services such as printers, scanners and other office tools, as well as technical support to solve any technological problems.'
    },
    {
      number: 5,
      title: language === 'it' ? 'SPAZI COMUNI E DI RELAX' : 'COMMON AND RELAXATION SPACES',
      description: language === 'it' 
        ? 'Aree dedicate alle pause, con cucine attrezzate o spazi all\'aperto, permettendo di staccare dalla routine lavorativa e ricaricare le energie in un ambiente piacevole.'
        : 'Areas dedicated to breaks, with equipped kitchens or outdoor spaces, allowing you to disconnect from the work routine and recharge your energy in a pleasant environment.'
    }
  ];
  
  return (
    <PageContainer
      initial="initial"
      animate="animate"
      exit="exit"
      variants={pageVariants}
    >
      <Helmet>
        <title>{language === 'it' ? 'Smart Working | J-O-H' : 'Smart Working | J-O-H'}</title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Spazi di lavoro flessibili e attrezzati in immobili per affitti brevi, perfetti per lo smart working e le esigenze di lavoro remoto' 
            : 'Flexible and equipped work spaces in properties for short-term rentals, perfect for smart working and remote work needs'} 
        />
      </Helmet>
      
      <HeroSection variants={heroVariants}>
        <BackLink to="/services/pop-up">
          {language === 'it' ? 'Indietro' : 'Back'}
        </BackLink>
        <HeroContent>
          <PageTitle>SMART WORKING</PageTitle>
        </HeroContent>
      </HeroSection>
      
      <ContentSection>
        <ContentContainer>
          <Description>
            {language === 'it' 
              ? 'Gli immobili per affitti brevi rappresentano una soluzione ideale per lo smart working, offrendo spazi confortevoli, attrezzati e flessibili per il lavoro remoto. Che tu sia un libero professionista, un dipendente in smart working o un team alla ricerca di un ambiente stimolante, i nostri spazi possono essere trasformati in uffici temporanei perfettamente adattati alle tue esigenze.'
              : 'Short-term rental properties represent an ideal solution for smart working, offering comfortable, equipped and flexible spaces for remote work. Whether you\'re a freelancer, an employee in smart working or a team looking for a stimulating environment, our spaces can be transformed into temporary offices perfectly adapted to your needs.'}
          </Description>
          
          <SectionTitle>
            {language === 'it' ? 'VANTAGGI DELLO SMART WORKING IN IMMOBILI PER AFFITTI BREVI' : 'ADVANTAGES OF SMART WORKING IN PROPERTIES FOR SHORT-TERM RENTALS'}
          </SectionTitle>
          
          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard key={index}>
                <BenefitIcon>
                  {benefit.icon}
                </BenefitIcon>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>
          
          <SectionTitle>
            {language === 'it' ? 'COSA OFFRIAMO' : 'WHAT WE OFFER'}
          </SectionTitle>
          
          <FeaturesContainer>
            {features.map((feature) => (
              <FeatureItem key={feature.number}>
                <FeatureNumber>{feature.number}</FeatureNumber>
                <FeatureContent>
                  <FeatureTitle>{feature.title}</FeatureTitle>
                  <FeatureDescription>{feature.description}</FeatureDescription>
                </FeatureContent>
              </FeatureItem>
            ))}
          </FeaturesContainer>
          
          <Description>
            {language === 'it' 
              ? 'Gli immobili per affitti brevi trasformati in spazi per lo smart working offrono un\'alternativa flessibile e confortevole all\'ufficio tradizionale o al lavoro da casa. Con la giusta combinazione di tecnologia, comfort e servizi, questi spazi possono migliorare significativamente la produttività e il benessere durante le ore di lavoro remoto.'
              : 'Short-term rental properties transformed into spaces for smart working offer a flexible and comfortable alternative to the traditional office or working from home. With the right combination of technology, comfort and services, these spaces can significantly improve productivity and well-being during remote working hours.'}
          </Description>
          
          <CTASection>
            <Button variant="primary" to="/contact">
              {language === 'it' ? 'Prenota il tuo spazio di lavoro' : 'Book your workspace'}
            </Button>
          </CTASection>
        </ContentContainer>
      </ContentSection>
      
      <Footer />
    </PageContainer>
  );
};

export default SmartWorking; 